import { useEffect, useRef } from "react";

import { Paper, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import PropTypes from "prop-types";
import { Controller, useFormContext, useWatch } from "react-hook-form";

import { UserOutletsSelect } from "@/features/outlets";
import { CustomDatePicker, CustomInputField } from "@/features/ui";
import { OutletWarehouseSelect } from "@/features/warehouses";

import { InventoryAdjustmentAccountSelect } from "../inventory-adjustment-account-select";
import { useInventoryAdjustmentAccounts } from "../use-inventory-adjustment-accounts";

export const GeneralInformationSection = ({ editMode = false }) => {
  const {
    control,
    register,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext();

  const outlet = useWatch({ control, name: "outlet" });
  const stockAdjustmentProductVariants = useWatch({
    control,
    name: "stockAdjustmentProductVariants",
  });

  const hasLines = stockAdjustmentProductVariants?.length > 0;

  const { accounts, loading: accountsLoading } =
    useInventoryAdjustmentAccounts();

  useEffect(() => {
    if (editMode) return;
    setValue("warehouse", null);
  }, [outlet?.id, editMode, setValue]);

  // Autofill the first configured adjustment account once, on create, when the
  // user hasn't picked one yet.
  const didAutofillAccount = useRef(false);
  useEffect(() => {
    if (editMode || didAutofillAccount.current || accounts.length === 0) return;

    didAutofillAccount.current = true;
    if (!getValues("adjustmentChartOfAccount")) {
      setValue("adjustmentChartOfAccount", accounts[0]);
    }
  }, [editMode, accounts, getValues, setValue]);

  return (
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      <Typography variant="h6" gutterBottom>
        Informasi Umum
      </Typography>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Controller
            name="outlet"
            control={control}
            render={({ field, fieldState }) => (
              <UserOutletsSelect
                {...field}
                disabled={editMode || hasLines}
                TextFieldProps={{
                  required: true,
                  label: "Outlet",
                  error: fieldState.invalid,
                  helperText:
                    hasLines && !editMode
                      ? "Tidak dapat mengubah outlet karena sudah memiliki baris produk"
                      : fieldState.error?.message,
                }}
              />
            )}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Controller
            name="warehouse"
            control={control}
            render={({ field, fieldState }) => (
              <OutletWarehouseSelect
                value={field.value}
                onChange={field.onChange}
                disabled={editMode || hasLines || !outlet}
                outletId={outlet?.id}
                TextFieldProps={{
                  required: true,
                  label: "Gudang",
                  error: fieldState.invalid,
                  helperText:
                    hasLines && !editMode
                      ? "Tidak dapat mengubah gudang karena sudah memiliki baris produk"
                      : fieldState.error?.message,
                  InputProps: {
                    placeholder: "Pilih Gudang",
                  },
                }}
              />
            )}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Controller
            name="transactionDate"
            control={control}
            render={({ field, fieldState }) => (
              <CustomDatePicker
                value={field.value}
                onChange={field.onChange}
                required
                label="Tanggal Transaksi"
                error={fieldState.invalid}
                helperText={fieldState.error?.message}
                InputProps={{
                  disableFuture: true,
                }}
              />
            )}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Controller
            name="adjustmentChartOfAccount"
            control={control}
            render={({ field, fieldState }) => (
              <InventoryAdjustmentAccountSelect
                value={field.value}
                onChange={field.onChange}
                options={accounts}
                loading={accountsLoading}
                disabled={editMode}
                TextFieldProps={{
                  required: true,
                  label: "Akun Penyesuaian",
                  error: fieldState.invalid,
                  helperText: fieldState.error?.message,
                  InputProps: {
                    placeholder: "Pilih Akun",
                  },
                }}
              />
            )}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, lg: 8 }}>
          <CustomInputField
            name="note"
            label="Catatan"
            register={register}
            error={Boolean(errors.note)}
            helperText={errors.note?.message}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

GeneralInformationSection.propTypes = {
  editMode: PropTypes.bool,
};
